import { Capacitor } from '@capacitor/core'

import { applyBaseTags, buildBeforeBreadcrumb, buildBeforeSend } from './scope'

function readSentryConfig() {
  return CONFIG?.sentry ?? null
}

function buildCommonOptions(sentryConfig) {
  return {
    dsn: sentryConfig.dsn,
    environment: sentryConfig.environment,
    release: GITSHA,
    tracesSampleRate: sentryConfig.tracesSampleRate ?? 0,
    beforeSend: buildBeforeSend(),
    beforeBreadcrumb: buildBeforeBreadcrumb(),
  }
}

async function initWeb(app, router, sentryConfig) {
  const Sentry = await import('@sentry/vue')
  // Replay is only supported by Sentry SaaS, not GlitchTip — gate behind config flag
  // so we can flip it on if we ever change vendor without re-adding the code.
  const integrations = [Sentry.browserTracingIntegration({ router })]
  if (sentryConfig.replayEnabled) {
    integrations.push(Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }))
  }
  Sentry.init({
    app,
    ...buildCommonOptions(sentryConfig),
    integrations,
    replaysSessionSampleRate: sentryConfig.replayEnabled
      ? (sentryConfig.replaysSessionSampleRate ?? 0)
      : 0,
    replaysOnErrorSampleRate: sentryConfig.replayEnabled
      ? (sentryConfig.replaysOnErrorSampleRate ?? 0)
      : 0,
    trackComponents: true,
    logErrors: true,
  })
}

async function initNative(app, router, sentryConfig) {
  const [SentryCapacitor, SentryVue] = await Promise.all([
    import('@sentry/capacitor'),
    import('@sentry/vue'),
  ])
  SentryCapacitor.init(
    {
      ...buildCommonOptions(sentryConfig),
      enableNative: true,
      enableNativeCrashHandling: true,
    },
    init => SentryVue.init({
      ...init,
      app,
      integrations: [
        SentryVue.browserTracingIntegration({ router }),
      ],
      trackComponents: true,
      logErrors: true,
    }),
  )
}

async function initExtension(app, router, sentryConfig) {
  const Sentry = await import('@sentry/browser')
  Sentry.init({
    ...buildCommonOptions(sentryConfig),
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
  })

  if (app?.config) {
    app.config.errorHandler = (err, instance, info) => {
      Sentry.captureException(err, { extra: { vueInfo: info } })
      console.error(err)
    }
  }
}

export async function setupSentry(app, router) {
  const sentryConfig = readSentryConfig()
  if (!sentryConfig?.dsn) return

  try {
    if (Capacitor.isNativePlatform()) {
      await initNative(app, router, sentryConfig)
    } else if (BUILD_TARGET === 'extension') {
      await initExtension(app, router, sentryConfig)
    } else {
      await initWeb(app, router, sentryConfig)
    }

    applyBaseTags()
  } catch (error) {
    console.error('[Sentry] Failed to initialize', error)
  }
}
